import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { NotificationStatus } from '@brickup/shared/enums'
import type {
  Notification,
  UpdateNotificationDto,
} from '@brickup/shared/models'
import { apiClient } from '@/api/client'
import { useMutationWithInvalidation } from '@/hooks/use-mutation-with-invalidation'

// ==================== API Functions ====================

export async function findNotifications(params?: {
  skip?: number
  take?: number
  status?: NotificationStatus
}) {
  const response = await apiClient.get<Notification[]>('/notifications', {
    params,
  })
  return response.data
}

export async function getUnreadCount() {
  const response = await apiClient.get<{ count: number }>(
    '/notifications/unread/count',
  )
  return response.data
}

export async function patchNotification({
  id,
  data,
}: {
  id: number
  data: UpdateNotificationDto
}) {
  const response = await apiClient.patch<Notification>(
    `/notifications/${id}`,
    data,
  )
  return response.data
}

export async function markNotificationAsRead(id: number) {
  const response = await apiClient.patch<Notification>(
    `/notifications/${id}/read`,
  )
  return response.data
}

export async function markAllAsRead() {
  const response = await apiClient.patch<{ count: number }>(
    '/notifications/mark-all-read',
  )
  return response.data
}

export async function deleteNotification(id: number) {
  const response = await apiClient.delete<Notification>(`/notifications/${id}`)
  return response.data
}

// ==================== Custom Hooks ====================

/**
 * Query hook for unread notification count
 * - Auto-refetches every 30 seconds
 * - Refetches on window focus
 */
export function useUnreadCount() {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: getUnreadCount,
    // The badge is polled, so a remount reads the cache rather than asking
    // again — the header this sits in is rebuilt on every navigation.
    staleTime: 30_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  })
}

/**
 * Query hook for notifications list
 * - Refetches on window focus
 * - Excludes deleted notifications by default
 */
export function useNotifications(params?: {
  skip?: number
  take?: number
  status?: NotificationStatus
}) {
  return useQuery({
    queryKey: ['notifications', params],
    queryFn: () => findNotifications(params),
    refetchOnWindowFocus: true,
  })
}

/**
 * Mutation hook to mark a notification as read
 * - Optimistically updates the cache
 * - Invalidates queries on success
 */
export function useMarkAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: markNotificationAsRead,
    onMutate: async (notificationId: number) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['notifications'] })

      // Snapshot previous values
      const previousNotifications = queryClient.getQueryData(['notifications'])
      const previousCount = queryClient.getQueryData([
        'notifications',
        'unread-count',
      ])

      // Optimistically update notification status
      // Note: setQueriesData matches multiple keys, so check if data is an array
      queryClient.setQueriesData(
        { queryKey: ['notifications'] },
        (old: unknown) => {
          if (!old || !Array.isArray(old)) return old
          return old.map((n: Notification) =>
            n.id === notificationId
              ? { ...n, status: NotificationStatus.READ }
              : n,
          )
        },
      )

      // Optimistically decrement unread count
      queryClient.setQueryData(
        ['notifications', 'unread-count'],
        (old: { count: number } | undefined) => {
          if (!old) return old
          return { count: Math.max(0, old.count - 1) }
        },
      )

      return { previousNotifications, previousCount }
    },
    onError: (_err, _notificationId, context) => {
      // Rollback on error
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          ['notifications'],
          context.previousNotifications,
        )
      }
      if (context?.previousCount) {
        queryClient.setQueryData(
          ['notifications', 'unread-count'],
          context.previousCount,
        )
      }
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({
        queryKey: ['notifications', 'unread-count'],
      })
    },
  })
}

/**
 * Mutation hook to mark all notifications as read
 * - Invalidates all notification queries
 */
export function useMarkAllAsRead() {
  return useMutationWithInvalidation(markAllAsRead, [
    ['notifications'],
    ['notifications', 'unread-count'],
  ])
}

/**
 * Mutation hook to toggle notification read status
 * - Optimistically updates the cache
 * - Invalidates queries on success
 */
export function useToggleNotificationStatus() {
  return useMutationWithInvalidation(
    ({ id, data }: { id: number; data: UpdateNotificationDto }) =>
      patchNotification({ id, data }),
    [['notifications'], ['notifications', 'unread-count']],
  )
}

/**
 * Mutation hook to delete a notification
 * - Optimistically removes from cache
 * - Invalidates queries on success
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteNotification,
    onMutate: async (notificationId: number) => {
      await queryClient.cancelQueries({ queryKey: ['notifications'] })

      const previousNotifications = queryClient.getQueryData(['notifications'])

      // Optimistically remove notification from cache
      // Note: setQueriesData matches multiple keys like ['notifications', params] and ['notifications', 'unread-count']
      // so we check if data is an array before filtering
      queryClient.setQueriesData(
        { queryKey: ['notifications'] },
        (old: unknown) => {
          if (!old || !Array.isArray(old)) return old
          return old.filter((n: Notification) => n.id !== notificationId)
        },
      )

      return { previousNotifications }
    },
    onError: (_err, _notificationId, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          ['notifications'],
          context.previousNotifications,
        )
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({
        queryKey: ['notifications', 'unread-count'],
      })
    },
  })
}
